import React, { CSSProperties, useContext } from "react";
import Select from "common/Select/Select";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { ErrorText, FormFieldTitle } from "features/bots/common/styles";
import useAmountSelector from "@shared/features/bots/BotForm/useAmountSelector";
import { useIsOnboarding } from "utils/hooks/misc";
import Flex from "common/Boxes/Flex";
import styled, { useTheme } from "styled-components";
import Text from "common/Typography/Text";
import OverrideToggle from "features/bots/BotForm/common/OverrideToggle";
import { BotType, OverrideFieldType } from "@shared/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle";
import { ToggleField } from "@shared/features/bots/BotForm/common/types";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid.tsx";
import useMeasure from "utils/hooks/useMeasure.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";
import { LanguageNameSpaces } from "@shared/types/common/common.types";
import { DEFAULT_FIRST_ENTRY_TAKE_PROFIT } from "@shared/features/bots/BotForm/common/constants.ts";
import {
  AMOUNT_PER_TRADE_INPUT_ID,
  AMOUNT_PER_TRADE_TYPE_SELECT_ID,
} from "testIds.ts";
import { translateOptionName } from "@shared/common/i18n/optionNames";

interface AmountSelectorProps {
  isCopyPublicBotSummary?: boolean;
  titleStyle?: CSSProperties;
  hideErrors?: boolean;
  isBacktestModal?: boolean;
}

const AmountSelector = ({
  isCopyPublicBotSummary,
  titleStyle,
  hideErrors,
  isBacktestModal,
}: AmountSelectorProps) => {
  const theme = useTheme();
  const isOnboarding = useIsOnboarding();
  const { isChannelConfiguration, botType } = useContext(BotFormContext)!;
  const {
    minAmountErrorMessage,
    isWarning,
    isError,
    amountTypeData,
    amountValueData,
    helperData,
    amountPercentBaselineData,
    amountCustomBaseValueData,
    baselineErrorData,
  } = useAmountSelector({
    isCopyPublicBotSummary,
    isOnboarding,
    isBacktestModal,
  });
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const numColumns =
    width < 650 || !amountPercentBaselineData.shouldDisplay ? 1 : 2;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;

  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const title = (
    <FormFieldTitle style={titleStyle}>
      {t("labels.amount_per_trade")}
      <HelperTooltip {...helperData} />
    </FormFieldTitle>
  );

  const amountValueInput = (
    <InputWrapper>
      <CustomInputNumber
        {...amountValueData}
        data-testid={AMOUNT_PER_TRADE_INPUT_ID}
        useDebounce={true}
        stringMode={true}
      />
      <Text type="secondary">{amountValueData.suffix}</Text>
    </InputWrapper>
  );

  const amountTypeSelect = (
    <StyledSelect
      data-testid={AMOUNT_PER_TRADE_TYPE_SELECT_ID}
      value={amountTypeData.value}
      onChange={amountTypeData.onChange}
      bordered={amountTypeData.bordered}
      placeholder={amountTypeData.placeholder}
      options={amountTypeData.options?.map((option) => ({
        value: option.value,
        // Channel configuration options are translation keys; options from
        // the server are already display text (kept as the fallback).
        label: t(option.name as TranslationKeys<LanguageNameSpaces.BOTS>, {
          defaultValue: option.name,
        }),
      }))}
      mobileTitle={amountTypeData.title}
      showSearch={false}
      $isSplitInput={true}
      popupMatchSelectWidth={false}
      style={{ flex: `1 1 ${SELECT_MIN_WIDTH}px` }}
    />
  );

  const amountPercentBaseline = (
    <StyledSelect
      value={amountPercentBaselineData.value}
      onChange={amountPercentBaselineData.onChange}
      bordered={amountPercentBaselineData.bordered}
      options={amountPercentBaselineData.options?.map((option) => ({
        value: option.value,
        label: translateOptionName(option.name),
      }))}
      mobileTitle={amountPercentBaselineData.title}
      showSearch={false}
      $borderOnRight={true}
      $isSplitInput={amountCustomBaseValueData.shouldDisplay}
      popupMatchSelectWidth={false}
      style={{ flex: `1 1 ${SELECT_MIN_WIDTH}px` }}
    />
  );

  const amountBaselineCustomInput = amountCustomBaseValueData.shouldDisplay ? (
    <InputWrapper>
      <CustomInputNumber
        value={amountCustomBaseValueData.value}
        precision={amountCustomBaseValueData.precision}
        onChange={amountCustomBaseValueData.onChange}
        placeholder={amountCustomBaseValueData.placeholder}
        bordered={amountCustomBaseValueData.bordered}
        useDebounce={true}
        stringMode={true}
      />
      <Text type="secondary">{amountCustomBaseValueData.suffix}</Text>
    </InputWrapper>
  ) : undefined;

  const getPercentBaseline = () => {
    if (!amountPercentBaselineData.shouldDisplay) return;
    return (
      <div>
        <FormFieldTitle>{amountPercentBaselineData.title}</FormFieldTitle>
        <AmountWrapper
          $showWarning={baselineErrorData?.isWarning && !hideErrors}
          $showError={baselineErrorData?.isError && !hideErrors}
        >
          {amountPercentBaseline}
          {amountBaselineCustomInput}
        </AmountWrapper>
        {baselineErrorData?.baselineError && !hideErrors && (
          <ErrorText
            style={{
              color: baselineErrorData?.isWarning
                ? theme.colors.gold
                : undefined,
            }}
          >
            {baselineErrorData?.baselineError}
          </ErrorText>
        )}
      </div>
    );
  };

  const getAmountSelect = () => {
    return (
      <div>
        {!isChannelConfiguration && !isTradingViewSignalsBot && title}
        <AmountWrapper
          $showWarning={isWarning && !hideErrors}
          $showError={isError && !hideErrors}
        >
          {amountValueInput}
          {amountTypeSelect}
        </AmountWrapper>
        {minAmountErrorMessage && !hideErrors && (
          <ErrorText
            style={{
              color: isWarning && !isError ? theme.colors.gold : undefined,
            }}
          >
            {minAmountErrorMessage}
          </ErrorText>
        )}
        {amountTypeData.warning && !hideErrors && (
          <ErrorText
            style={{
              color: theme.colors.gold,
            }}
          >
            {amountTypeData.warning}
          </ErrorText>
        )}
      </div>
    );
  };

  const content = (
    <>
      <EqualColumnsGrid numColumns={numColumns} ref={measureRef}>
        {getAmountSelect()}
        {getPercentBaseline()}
      </EqualColumnsGrid>
      {!isCopyPublicBotSummary && (
        <OverrideToggle overrideField={OverrideFieldType.AMOUNT_PER_TRADE} />
      )}
    </>
  );

  if (isChannelConfiguration || isTradingViewSignalsBot) {
    return (
      <TitleWithToggle
        title={t("labels.amount_per_trade")}
        content={content}
        fieldType={ToggleField.AMOUNT_PER_TRADE}
        getDefaultValue={
          isTradingViewSignalsBot
            ? () => DEFAULT_FIRST_ENTRY_TAKE_PROFIT
            : undefined
        }
      />
    );
  }

  return content;
};

export default AmountSelector;

const CUSTOM_INPUT_MIN_WIDTH = 115;
const SELECT_MIN_WIDTH = 165;

export const AmountWrapper = styled(Flex)<{
  $showWarning?: boolean;
  $showError?: boolean;
}>`
  border: ${(p) => p.theme.borders.light};
  background-color: ${(p) => p.theme.colors.cardBgColor};
  border-radius: 8px;
  width: 100%;
  ${(p) =>
    p.$showWarning &&
    `
  border-color: ${p.theme.colors.gold};
  `}
  ${(p) =>
    p.$showError &&
    `
  border-color: ${p.theme.colors.red};
  `}
`;
export const StyledSelect = styled(Select)<{
  $borderOnRight?: boolean;
  $isSplitInput?: boolean;
}>`
  width: ${(p) => (p.$isSplitInput ? 50 : 100)}%;
  ${(p) => {
    if (!p.$isSplitInput) return;
    return p.$borderOnRight
      ? `
      border-right: ${p.theme.borders.light};
    `
      : `
    border-left: ${p.theme.borders.light};
  `;
  }};
`;
export const InputWrapper = styled(Flex)`
  flex: 1 1 ${CUSTOM_INPUT_MIN_WIDTH}px;
  padding: 0 11px;
`;
